import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { en } from '../translations/en';
import { no } from '../translations/no';
import { sr } from '../translations/sr';
import { es } from '../translations/es';
import { de } from '../translations/de';
import { id } from '../translations/id';

const translations = { en, no, sr, es, de, id };
const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  // Initialize with stored language or browser language or default to English
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('language');
    if (stored) return stored;
    
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'no', 'sr', 'es', 'de', 'id'].includes(browserLang)) return browserLang;
    
    return 'en';
  });

  // Update storage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  }, []);

  const translate = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }

    if (typeof value === 'string') {
      return Object.entries(params).reduce((str, [key, val]) => 
        str.replace(`{${key}}`, val), value);
    }
    
    return key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
